// DEPLOY: /var/www/woody.wtf/frontend/src/pages/AdminEtf.js
// Admin: ETF providers, holdings URLs, ingestion status.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import api from '../api';

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const s = {
  page:    { minHeight: '100vh', background: 'var(--bg)' },
  nav: {
    background: 'var(--surface)', borderBottom: '1px solid var(--border)',
    padding: '0 32px', height: '56px', display: 'flex',
    alignItems: 'center', justifyContent: 'space-between',
  },
  logo:    { fontFamily: "'JetBrains Mono', monospace", fontSize: '13px', fontWeight: 500, letterSpacing: '0.1em', color: 'var(--accent)', textTransform: 'uppercase' },
  navRight:{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '13px', color: 'var(--text-muted)' },
  navBtn:  { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)', fontSize: '13px', fontFamily: "'Space Grotesk', sans-serif", padding: '6px 12px', borderRadius: 'var(--radius)' },
  content: { maxWidth: '1400px', margin: '0 auto', padding: '40px 32px' },
  heading: { fontSize: '22px', fontWeight: 600, letterSpacing: '-0.02em', marginBottom: '8px' },
  sub:     { fontSize: '13px', color: 'var(--text-muted)', marginBottom: '32px' },
  tabs:    { display: 'flex', gap: '4px', marginBottom: '28px', borderBottom: '1px solid var(--border)', paddingBottom: '0' },
  tab:     (active) => ({
    padding: '8px 18px', fontSize: '13px', fontWeight: 500, cursor: 'pointer',
    background: 'none', border: 'none', fontFamily: "'Space Grotesk', sans-serif",
    color: active ? 'var(--accent)' : 'var(--text-muted)',
    borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
    marginBottom: '-1px',
  }),
  section: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'calc(var(--radius) * 2)', padding: '24px', marginBottom: '24px' },
  sectionTitle: { fontSize: '14px', fontWeight: 600, marginBottom: '16px', color: 'var(--text)' },
  table:   { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  th:      { textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)', fontWeight: 500, fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', letterSpacing: '0.05em', textTransform: 'uppercase', whiteSpace: 'nowrap' },
  td:      { padding: '9px 10px', borderBottom: '1px solid var(--border)', verticalAlign: 'middle' },
  btn:     { padding: '5px 11px', borderRadius: 'var(--radius)', border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: 500, fontFamily: "'Space Grotesk', sans-serif" },
  input:   { padding: '6px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: '12px', fontFamily: "'Space Grotesk', sans-serif", background: 'var(--bg)', color: 'var(--text)', outline: 'none', width: '100%', boxSizing: 'border-box' },
  row:     { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' },
  badge:   (status) => {
    const map = {
      ok:      { bg: 'var(--success-dim)', color: 'var(--success)' },
      failed:  { bg: 'var(--error-dim)',   color: 'var(--error)'   },
      pending: { bg: 'var(--warning-dim)',            color: 'var(--warning)'         },
      no_url:  { bg: 'var(--border)',      color: 'var(--text-muted)' },
      manual:  { bg: 'var(--accent-dim)',  color: 'var(--accent)'  },
    };
    const c = map[status] || map.no_url;
    return {
      display: 'inline-block', padding: '2px 7px', borderRadius: '4px',
      fontSize: '10px', fontWeight: 600, fontFamily: "'JetBrains Mono', monospace",
      textTransform: 'uppercase', letterSpacing: '0.04em',
      background: c.bg, color: c.color,
    };
  },
  err:  { color: 'var(--error)',   fontSize: '13px', marginTop: '8px' },
  ok:   { color: 'var(--success)', fontSize: '13px', marginTop: '8px' },
  filterBar: { display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' },
  searchInput: { padding: '7px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: '13px', fontFamily: "'Space Grotesk', sans-serif", background: 'var(--bg)', color: 'var(--text)', outline: 'none', width: '220px' },
  select: { padding: '7px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: '13px', fontFamily: "'Space Grotesk', sans-serif", background: 'var(--bg)', color: 'var(--text)', outline: 'none' },
};

function Btn({ onClick, color = 'var(--accent)', textColor = 'var(--on-accent)', children, disabled, style = {} }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ ...s.btn, background: color, color: textColor, opacity: disabled ? 0.5 : 1, ...style }}>
      {children}
    </button>
  );
}

function fmt(iso) { return iso ? new Date(iso).toLocaleDateString() : '—'; }
function fmtUrl(url) {
  if (!url) return '—';
  const short = url.length > 55 ? '...' + url.slice(-50) : url;
  return <span title={url} style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', color: 'var(--text-muted)' }}>{short}</span>;
}

// ---------------------------------------------------------------------------
// Provider form (add / edit)
// ---------------------------------------------------------------------------
function ProviderForm({ initial = {}, onSave, onCancel, flash }) {
  const [form, setForm] = useState({
    name: initial.name || '',
    country: initial.country || 'AU',
    base_url: initial.base_url || '',
    url_pattern: initial.url_pattern || '',
    notes: initial.notes || '',
  });
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const isEdit = !!initial.provider_id;

  const save = async () => {
    try {
      const payload = { ...form, ...(isEdit ? { provider_id: initial.provider_id } : {}) };
      await api.post(isEdit ? '/etf/admin/providers/update' : '/etf/admin/providers/add', payload);
      flash(isEdit ? 'Provider updated' : 'Provider added');
      onSave();
    } catch (e) {
      flash(e.response?.data?.error || 'Failed', true);
    }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 1fr 1fr', gap: '8px', alignItems: 'end', marginBottom: '12px' }}>
      <div>
        <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '4px', fontFamily: "'JetBrains Mono', monospace", textTransform: 'uppercase' }}>Name</div>
        <input style={s.input} value={form.name} onChange={set('name')} placeholder="BetaShares" />
      </div>
      <div>
        <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '4px', fontFamily: "'JetBrains Mono', monospace", textTransform: 'uppercase' }}>Country</div>
        <select style={{ ...s.input }} value={form.country} onChange={set('country')}>
          <option>AU</option><option>US</option>
        </select>
      </div>
      <div>
        <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '4px', fontFamily: "'JetBrains Mono', monospace", textTransform: 'uppercase' }}>Base URL</div>
        <input style={s.input} value={form.base_url} onChange={set('base_url')} placeholder="https://www.betashares.com.au/files/csv/" />
      </div>
      <div>
        <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '4px', fontFamily: "'JetBrains Mono', monospace", textTransform: 'uppercase' }}>URL Pattern</div>
        <input style={s.input} value={form.url_pattern} onChange={set('url_pattern')} placeholder="[ticker]_Portfolio_Holdings.csv" />
      </div>
      <div style={{ gridColumn: '1 / -1' }}>
        <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '4px', fontFamily: "'JetBrains Mono', monospace", textTransform: 'uppercase' }}>Notes</div>
        <input style={s.input} value={form.notes} onChange={set('notes')} placeholder="Optional admin notes" />
      </div>
      <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '8px' }}>
        <Btn onClick={save}>{isEdit ? 'Save Changes' : 'Add Provider'}</Btn>
        {onCancel && <Btn onClick={onCancel} color="var(--surface)" textColor="var(--text)">Cancel</Btn>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Providers tab
// ---------------------------------------------------------------------------
function ProvidersTab({ flash }) {
  const [providers, setProviders] = useState([]);
  const [editId, setEditId]       = useState(null);
  const [showAdd, setShowAdd]     = useState(false);

  const load = useCallback(async () => {
    const r = await api.get('/etf/admin/providers');
    setProviders(r.data.providers);
  }, []);

  useEffect(() => { load(); }, [load]);

  const del = async (pid) => {
    if (!window.confirm('Delete this provider? ETFs assigned to it will be unlinked.')) return;
    try {
      await api.post('/etf/admin/providers/delete', { provider_id: pid });
      flash('Provider deleted');
      load();
    } catch (e) { flash(e.response?.data?.error || 'Failed', true); }
  };

  return (
    <div>
      <div style={s.section}>
        <div style={s.sectionTitle}>Providers ({providers.length})</div>
        <table style={s.table}>
          <thead>
            <tr>
              {['Name','Country','Base URL','URL Pattern','ETFs','Notes','Added',''].map(h => (
                <th key={h} style={s.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {providers.map(p => (
              <React.Fragment key={p.provider_id}>
                <tr>
                  <td style={s.td}><strong>{p.name}</strong></td>
                  <td style={s.td}><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{p.country}</span></td>
                  <td style={s.td}>{fmtUrl(p.base_url)}</td>
                  <td style={s.td}><span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px' }}>{p.url_pattern || '—'}</span></td>
                  <td style={{ ...s.td, textAlign: 'center' }}>{p.etf_count}</td>
                  <td style={{ ...s.td, color: 'var(--text-muted)', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.notes}>{p.notes || '—'}</td>
                  <td style={s.td}>{fmt(p.created_at)}</td>
                  <td style={s.td}>
                    <div style={s.row}>
                      <Btn onClick={() => setEditId(editId === p.provider_id ? null : p.provider_id)} color="var(--surface)" textColor="var(--text)">Edit</Btn>
                      <Btn onClick={() => del(p.provider_id)} color="var(--error-dim)" textColor="var(--error)">Delete</Btn>
                    </div>
                  </td>
                </tr>
                {editId === p.provider_id && (
                  <tr>
                    <td colSpan={8} style={{ ...s.td, background: 'var(--bg)', padding: '16px' }}>
                      <ProviderForm initial={p} onSave={() => { setEditId(null); load(); }} onCancel={() => setEditId(null)} flash={flash} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div style={s.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div style={s.sectionTitle}>Add Provider</div>
          <Btn onClick={() => setShowAdd(v => !v)} color="var(--surface)" textColor="var(--text)">{showAdd ? 'Hide' : 'Show'}</Btn>
        </div>
        {showAdd && <ProviderForm onSave={() => { setShowAdd(false); load(); }} flash={flash} />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ETF row — inline URL edit
// ---------------------------------------------------------------------------
function EtfRow({ etf, providers, onRefresh, flash }) {
  const [editing, setEditing]   = useState(false);
  const [urlInput, setUrlInput] = useState(etf.holdings_url || '');
  const [busy, setBusy]         = useState(false);

  const saveUrl = async () => {
    setBusy(true);
    try {
      await api.post('/etf/admin/etfs/update-url', { instrument_id: etf.instrument_id, holdings_url: urlInput });
      flash('URL saved');
      setEditing(false);
      onRefresh();
    } catch (e) { flash(e.response?.data?.error || 'Failed', true); }
    finally { setBusy(false); }
  };

  const rebuild = async () => {
    setBusy(true);
    try {
      const r = await api.post('/etf/admin/etfs/rebuild-url', { instrument_id: etf.instrument_id });
      flash(`URL rebuilt: ${r.data.url}`);
      onRefresh();
    } catch (e) { flash(e.response?.data?.error || 'Failed', true); }
    finally { setBusy(false); }
  };

  const setStatus = async (status) => {
    try {
      await api.post('/etf/admin/etfs/set-status', { instrument_id: etf.instrument_id, status });
      flash('Status updated');
      onRefresh();
    } catch (e) { flash(e.response?.data?.error || 'Failed', true); }
  };

  const assignProvider = async (provider_id) => {
    try {
      await api.post('/etf/admin/etfs/upsert-detail', { instrument_id: etf.instrument_id, provider_id: parseInt(provider_id) || null });
      flash('Provider assigned');
      onRefresh();
    } catch (e) { flash(e.response?.data?.error || 'Failed', true); }
  };

  const noDetail = etf.url_status === null; // no etf_details row yet

  return (
    <tr>
      <td style={s.td}><span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>{etf.symbol}</span></td>
      <td style={{ ...s.td, maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={etf.name}>{etf.name || '—'}</td>
      <td style={s.td}><span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px' }}>{etf.exchange}</span></td>
      <td style={s.td}>
        {noDetail ? (
          <select style={{ ...s.select, fontSize: '11px', padding: '4px 6px' }}
            defaultValue=""
            onChange={e => e.target.value && assignProvider(e.target.value)}>
            <option value="">Assign provider…</option>
            {providers.map(p => <option key={p.provider_id} value={p.provider_id}>{p.name}</option>)}
          </select>
        ) : (
          etf.provider_name || <span style={{ color: 'var(--text-muted)' }}>—</span>
        )}
      </td>
      <td style={s.td}>
        {noDetail
          ? <span style={s.badge('no_url')}>no detail</span>
          : <span style={s.badge(etf.url_status)}>{etf.url_status || 'no_url'}</span>
        }
      </td>
      <td style={{ ...s.td, textAlign: 'center' }}>{etf.holdings_count > 0 ? etf.holdings_count : '—'}</td>
      <td style={s.td}>{fmt(etf.last_fetched_at)}</td>
      <td style={{ ...s.td, minWidth: '220px' }}>
        {editing ? (
          <div style={{ display: 'flex', gap: '6px' }}>
            <input style={{ ...s.input, fontSize: '11px' }} value={urlInput} onChange={e => setUrlInput(e.target.value)} />
            <Btn onClick={saveUrl} disabled={busy}>Save</Btn>
            <Btn onClick={() => setEditing(false)} color="var(--surface)" textColor="var(--text)">✕</Btn>
          </div>
        ) : (
          fmtUrl(etf.holdings_url)
        )}
      </td>
      <td style={s.td}>
        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
          <Btn onClick={() => setEditing(true)} color="var(--surface)" textColor="var(--text)" disabled={busy}>URL</Btn>
          {!noDetail && etf.provider_name && !etf.url_is_manual && (
            <Btn onClick={rebuild} color="var(--surface)" textColor="var(--accent)" disabled={busy}>Rebuild</Btn>
          )}
          {!noDetail && etf.url_status !== 'pending' && (
            <Btn onClick={() => setStatus('pending')} color="var(--surface)" textColor="var(--text-muted)" disabled={busy}>Reset</Btn>
          )}
          {!noDetail && etf.url_status !== 'manual' && (
            <Btn onClick={() => setStatus('manual')} color="var(--surface)" textColor="var(--text-muted)" disabled={busy}>Manual</Btn>
          )}
        </div>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// ETFs tab
// ---------------------------------------------------------------------------
function EtfsTab({ flash }) {
  const [etfs, setEtfs]           = useState([]);
  const [providers, setProviders] = useState([]);
  const [search, setSearch]       = useState('');
  const [provFilter, setProvFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [exchFilter, setExchFilter] = useState('');
  const [loading, setLoading]     = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [er, pr] = await Promise.all([
        api.get('/etf/admin/etfs'),
        api.get('/etf/admin/providers'),
      ]);
      setEtfs(er.data.etfs);
      setProviders(pr.data.providers);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = etfs.filter(e => {
    if (search && !`${e.symbol} ${e.name}`.toLowerCase().includes(search.toLowerCase())) return false;
    if (provFilter && String(e.provider_id) !== provFilter && !(provFilter === '__none' && !e.provider_id)) return false;
    if (statusFilter && (e.url_status || 'no_url') !== statusFilter) return false;
    if (exchFilter && e.exchange !== exchFilter) return false;
    return true;
  });

  const statusCounts = etfs.reduce((acc, e) => {
    const k = e.url_status || 'no_detail';
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  return (
    <div>
      {/* Summary badges */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {Object.entries(statusCounts).map(([k, v]) => (
          <span key={k} style={{ ...s.badge(k), cursor: 'pointer', padding: '4px 10px', fontSize: '11px' }}
            onClick={() => setStatusFilter(statusFilter === k ? '' : k)}>
            {k}: {v}
          </span>
        ))}
      </div>

      {/* Filter bar */}
      <div style={s.filterBar}>
        <input style={s.searchInput} placeholder="Search symbol or name…" value={search} onChange={e => setSearch(e.target.value)} />
        <select style={s.select} value={provFilter} onChange={e => setProvFilter(e.target.value)}>
          <option value="">All providers</option>
          <option value="__none">No provider</option>
          {providers.map(p => <option key={p.provider_id} value={String(p.provider_id)}>{p.name}</option>)}
        </select>
        <select style={s.select} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          {['pending','ok','failed','no_url','manual','no_detail'].map(s2 => <option key={s2}>{s2}</option>)}
        </select>
        <select style={s.select} value={exchFilter} onChange={e => setExchFilter(e.target.value)}>
          <option value="">All exchanges</option>
          <option>AU</option><option>US</option>
        </select>
        <span style={{ fontSize: '12px', color: 'var(--text-muted)', marginLeft: 'auto' }}>
          {filtered.length} of {etfs.length} ETFs
        </span>
      </div>

      <div style={s.section}>
        {loading ? (
          <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Loading…</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={s.table}>
              <thead>
                <tr>
                  {['Symbol','Name','Exchange','Provider','Status','Holdings','Last Fetched','URL','Actions'].map(h => (
                    <th key={h} style={s.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(e => (
                  <EtfRow key={e.instrument_id} etf={e} providers={providers} onRefresh={load} flash={flash} />
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={9} style={{ ...s.td, color: 'var(--text-muted)', textAlign: 'center', padding: '32px' }}>No ETFs match the current filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------
export default function AdminEtf() {
  const { user } = useAuth();
  const navigate         = useNavigate();
  const [tab, setTab]    = useState('providers');
  const [msg, setMsg]    = useState('');
  const [err, setErr]    = useState('');

  useEffect(() => {
    if (user && !user.is_admin) navigate('/dashboard', { replace: true });
  }, [user, navigate]);


  const flash = (m, isErr = false) => {
    if (isErr) { setErr(m); setMsg(''); } else { setMsg(m); setErr(''); }
    setTimeout(() => { setMsg(''); setErr(''); }, 4000);
  };

  return (
    <div style={s.page}>
      <Navbar backTo={{ label: '← Admin', href: '/admin' }} />

      <div style={s.content}>
        <h1 style={s.heading}>ETF Management</h1>
        <p style={s.sub}>Manage ETF data providers, holdings URLs, and ingestion status.</p>

        {msg && <div style={{ ...s.ok, marginBottom: '16px' }}>✓ {msg}</div>}
        {err && <div style={{ ...s.err, marginBottom: '16px' }}>✕ {err}</div>}

        <div style={s.tabs}>
          {[['providers', 'Providers'], ['etfs', 'ETF Status']].map(([key, label]) => (
            <button key={key} style={s.tab(tab === key)} onClick={() => setTab(key)}>{label}</button>
          ))}
        </div>

        {tab === 'providers' && <ProvidersTab flash={flash} />}
        {tab === 'etfs'      && <EtfsTab flash={flash} />}
      </div>
    </div>
  );
}
